/**
 * Animal coats.
 *
 * Kate raised realism twice, unprompted: "when I play the game in the jungle, it doesn't
 * look realistic. I want to look realistic. Realistic like in real life." Howard sharpened
 * it — the animals must look like real animals, not cartoons.
 *
 * Flat solid colours were the single biggest thing making them read as cartoons, so every
 * species now gets a generated coat: fur direction, colour variation, and a normal map so
 * the fur catches the moving sun. Drawn on a canvas at load like every other texture here,
 * so nothing extra ships.
 */
import * as THREE from 'three'

export interface Pelt {
  map: THREE.CanvasTexture
  normalMap: THREE.CanvasTexture
}

export type Coat =
  | 'fur'        // bear, wolf, dog, cat, bunny, deer
  | 'short'      // camel, human skin
  | 'scales'     // chameleon
  | 'feathers'   // bird
  | 'smooth'     // dolphin, whale, fish
  | 'cloth'      // the human's shirt and trousers

/** Deterministic noise, so a wolf's coat is the same wolf coat every run. */
function rng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function heightToNormal(height: Float32Array, size: number, strength: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const image = ctx.createImageData(size, size)
  const at = (x: number, y: number): number =>
    height[((y + size) % size) * size + ((x + size) % size)]!
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength
      const len = Math.hypot(dx, dy, 1)
      const i = (y * size + x) * 4
      image.data[i] = ((-dx / len) * 0.5 + 0.5) * 255
      image.data[i + 1] = ((-dy / len) * 0.5 + 0.5) * 255
      image.data[i + 2] = (1 / len) * 0.5 * 255 + 127
      image.data[i + 3] = 255
    }
  }
  ctx.putImageData(image, 0, 0)
  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  return texture
}

/** Build a coat for one species. */
export function createPelt(baseColor: number, coat: Coat, seed: number): Pelt {
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const random = rng(seed)
  const base = new THREE.Color(baseColor)
  // See srgb255 in world/textures: a hex literal becomes linear on assignment, so
  // writing base.r * 255 into a canvas applies gamma a second time and every coat comes
  // out far darker than the colour it was given.
  const srgb = base.clone().convertLinearToSRGB()
  const [cr, cg, cb] = [srgb.r * 255, srgb.g * 255, srgb.b * 255]
  const height = new Float32Array(size * size)

  // Undercoat with a soft mottle, so the colour is never flat.
  const image = ctx.createImageData(size, size)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const patch = Math.sin(x * 0.045 + Math.sin(y * 0.03) * 2) * 0.5 + 0.5
      const grain = random()
      // Gentle: the previous range made every coat look like television static.
      const shade = 0.94 + patch * 0.1 + (grain - 0.5) * 0.03
      const i = (y * size + x) * 4
      image.data[i] = Math.min(255, cr * shade)
      image.data[i + 1] = Math.min(255, cg * shade)
      image.data[i + 2] = Math.min(255, cb * shade)
      image.data[i + 3] = 255
      height[y * size + x] = patch * 0.4 + grain * 0.15
    }
  }
  ctx.putImageData(image, 0, 0)

  ctx.lineCap = 'round'
  const dark = base.clone().multiplyScalar(0.86)
  const light = base.clone().lerp(new THREE.Color(0xffffff), 0.12)

  switch (coat) {
    case 'fur':
    case 'short': {
      // Thousands of short strokes lying in one direction — the thing that actually
      // reads as fur rather than paint.
      const strokes = coat === 'fur' ? 5200 : 2200
      const length = coat === 'fur' ? 9 : 4
      for (let i = 0; i < strokes; i++) {
        const x = random() * size
        const y = random() * size
        const lean = (random() - 0.5) * 0.5
        ctx.strokeStyle = random() > 0.5 ? dark.getStyle() : light.getStyle()
        // Low alpha and thin strokes: fur should be a grain you notice, not a pattern
        // that shouts. High-contrast hairs read as noise at gameplay distance.
        ctx.globalAlpha = 0.05 + random() * 0.08
        ctx.lineWidth = 0.6 + random() * 0.7
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(x + lean * length, y + length * (0.6 + random() * 0.7))
        ctx.stroke()
        const hx = Math.floor(x) % size
        const hy = Math.floor(y) % size
        height[hy * size + hx] = Math.min(1, (height[hy * size + hx] ?? 0) + 0.5)
      }
      break
    }
    case 'scales': {
      // Overlapping rounded scales in offset rows.
      const step = 13
      for (let row = 0; row * step < size + step; row++) {
        for (let col = 0; col * step < size + step; col++) {
          const x = col * step + (row % 2 ? step / 2 : 0)
          const y = row * step
          ctx.globalAlpha = 0.3 + random() * 0.3
          ctx.fillStyle = random() > 0.45 ? light.getStyle() : dark.getStyle()
          ctx.beginPath()
          ctx.ellipse(x, y, step * 0.52, step * 0.42, 0, 0, Math.PI * 2)
          ctx.fill()
          const hx = Math.floor(x) % size
          const hy = Math.floor(y) % size
          height[hy * size + hx] = 1
        }
      }
      break
    }
    case 'feathers': {
      // Layered arcs, like feather tips overlapping down the body.
      for (let i = 0; i < 900; i++) {
        const x = random() * size
        const y = random() * size
        const r = 4 + random() * 7
        ctx.globalAlpha = 0.2 + random() * 0.28
        ctx.strokeStyle = random() > 0.5 ? light.getStyle() : dark.getStyle()
        ctx.lineWidth = 1.1
        ctx.beginPath()
        ctx.arc(x, y, r, Math.PI * 0.15, Math.PI * 0.85)
        ctx.stroke()
        const hx = Math.floor(x) % size
        const hy = Math.floor(y) % size
        height[hy * size + hx] = Math.min(1, (height[hy * size + hx] ?? 0) + 0.6)
      }
      break
    }
    case 'cloth': {
      // A woven grid: warp and weft threads, slightly irregular, plus a few creases.
      // Flat colour is what made the human's clothes read as painted plastic.
      const pitch = 5
      for (let i = 0; i < size; i += pitch) {
        for (const [horizontal, offset] of [[true, 0], [false, Math.floor(pitch / 2)]] as const) {
          ctx.strokeStyle = (i / pitch) % 2 === 0 ? dark.getStyle() : light.getStyle()
          ctx.globalAlpha = 0.12 + random() * 0.1
          ctx.lineWidth = 1.6 + random() * 0.5
          ctx.beginPath()
          if (horizontal) { ctx.moveTo(0, i + offset); ctx.lineTo(size, i + offset + (random() - 0.5) * 2) }
          else { ctx.moveTo(i + offset, 0); ctx.lineTo(i + offset + (random() - 0.5) * 2, size) }
          ctx.stroke()
        }
        for (let x = 0; x < size; x += pitch) {
          const hx = x % size, hy = i % size
          height[hy * size + hx] = Math.min(1, (height[hy * size + hx] ?? 0) + 0.6)
        }
      }
      // Creases, where cloth actually folds.
      for (let i = 0; i < 22; i++) {
        ctx.strokeStyle = dark.getStyle()
        ctx.globalAlpha = 0.1 + random() * 0.08
        ctx.lineWidth = 2 + random() * 3
        const x = random() * size, y = random() * size
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(x + (random() - 0.5) * 90, y + (random() - 0.5) * 90)
        ctx.stroke()
      }
      break
    }
    case 'smooth': {
      // Almost nothing — a wet sheen with faint mottling along the flank.
      for (let i = 0; i < 260; i++) {
        const x = random() * size
        const y = random() * size
        ctx.globalAlpha = 0.08 + random() * 0.1
        ctx.fillStyle = random() > 0.5 ? light.getStyle() : dark.getStyle()
        ctx.beginPath()
        ctx.ellipse(x, y, 10 + random() * 22, 4 + random() * 8, random(), 0, Math.PI * 2)
        ctx.fill()
      }
      break
    }
  }
  ctx.globalAlpha = 1

  const map = new THREE.CanvasTexture(canvas)
  map.wrapS = THREE.RepeatWrapping
  map.wrapT = THREE.RepeatWrapping
  map.colorSpace = THREE.SRGBColorSpace
  map.anisotropy = 8

  const normalMap = heightToNormal(height, size, coat === 'smooth' ? 6 : coat === 'cloth' ? 9 : 12)
  return { map, normalMap }
}

/** Which coat a species wears. */
export function coatFor(speciesId: string): Coat {
  switch (speciesId) {
    case 'chameleon': return 'scales'
    case 'bird': return 'feathers'
    case 'dolphin': case 'whale': case 'fish': return 'smooth'
    case 'camel': case 'human': return 'short'
    default: return 'fur'
  }
}
